
/**
 * EmailJS template configuration and parameters
 */

export interface EmailFormData {
  name: string;
  email: string;
  phone: string;
  message: string;
  contactConsent?: boolean;
  privacyPolicyUrl?: string;
}

/**
 * Creates parameters for admin notification email
 */
export const createAdminTemplateParams = (formData: EmailFormData) => {
  // Make sure we're handling consent value correctly - treat any truthy value as consent given
  const hasConsent = Boolean(formData.contactConsent);
  
  const consentInfo = hasConsent 
    ? 'Ha otorgado consentimiento explícito para ser contactado' 
    : 'No ha otorgado consentimiento explícito para ser contactado';

  return {
    title: formData.name, // Used in the subject line {{title}}
    from_name: formData.name, // {{from_name}}
    from_email: formData.email, // {{from_email}}
    from_phone: formData.phone || 'Not provided', // {{from_phone}}
    message: formData.message || '', // {{message}}
    consent_info: consentInfo, // {{consent_info}}
    privacy_policy_url: formData.privacyPolicyUrl || 'https://gavinsurance.com/privacidad', // {{privacy_policy_url}}
    // Add explicit fields for template visibility
    consent_text: hasConsent 
      ? '✅ El cliente ha otorgado consentimiento explícito para ser contactado.' 
      : '❌ El cliente NO ha otorgado consentimiento explícito para ser contactado.',
    privacy_policy_text: "El cliente ha aceptado nuestra Política de Privacidad.",
    to_email: 'info@gavinsurance.com',
    reply_to: formData.email
  };
};

/**
 * Creates parameters for customer confirmation email
 */
export const createCustomerTemplateParams = (formData: EmailFormData) => {
  // Make sure we're handling consent value correctly - treat any truthy value as consent given
  const hasConsent = Boolean(formData.contactConsent);
  
  // Add privacy policy and consent information to the customer email
  const privacyPolicyText = "Al enviar este formulario, has aceptado nuestra Política de Privacidad.";
  const consentText = hasConsent 
    ? "Has otorgado tu consentimiento para que GAV Insurance te contacte por correo electrónico o teléfono respecto a tu solicitud."
    : "No has otorgado consentimiento para ser contactado.";

  // Include phone number in all possible formats for EmailJS template
  return {
    to_name: formData.name,
    to_email: formData.email,
    email: formData.email, // Fallback parameter
    // Add phone in ALL possible formats that EmailJS might look for
    phone: formData.phone || 'No proporcionado',
    to_phone: formData.phone || 'No proporcionado',
    from_phone: formData.phone || 'No proporcionado',
    client_phone: formData.phone || 'No proporcionado', // Client's phone number
    client_message: formData.message || '', // Add explicit client message field
    message: formData.message || '', // Keep for backward compatibility
    client_name: formData.name, // Add explicit client name field
    client_email: formData.email, // Add explicit client email
    company_name: 'GAV Insurance',
    company_email: 'info@gavinsurance.com',
    company_phone: '7865695142',
    reply_to: 'info@gavinsurance.com',
    privacy_policy: privacyPolicyText, // Add privacy policy text
    privacy_policy_url: formData.privacyPolicyUrl || 'https://gavinsurance.com/privacidad', // {{privacy_policy_url}}
    consent_info: consentText, // Add consent information
    // Add explicitly formatted fields for template visibility
    consent_text: hasConsent 
      ? "✅ Has otorgado tu consentimiento para que GAV Insurance te contacte." 
      : "❌ No has otorgado consentimiento para ser contactado.",
    privacy_policy_text: "Al enviar este formulario, has aceptado nuestra Política de Privacidad. " +
      "Para más información, visita nuestra página de Política de Privacidad.",
    privacy_section: `
      <div style="margin-top: 20px; padding: 15px; border: 1px solid #ddd; background-color: #f9f9f9; border-radius: 5px;">
        <p style="margin: 0;"><strong>Política de Privacidad:</strong> Al enviar este formulario, has aceptado nuestra 
        <a href="${formData.privacyPolicyUrl || 'https://gavinsurance.com/privacidad'}">Política de Privacidad</a>.</p>
        <p style="margin-top: 10px;"><strong>Consentimiento de Contacto:</strong> ${hasConsent 
          ? "✅ Has otorgado tu consentimiento para que GAV Insurance te contacte." 
          : "❌ No has otorgado consentimiento para ser contactado."}</p>
      </div>
    `
  };
};

/**
 * Creates alternate parameters for customer email (fallback)
 */
export const createAlternateCustomerParams = (baseParams: Record<string, string>) => {
  return {
    ...baseParams,
    to: baseParams.to_email || baseParams.email, // Some templates use 'to' instead of 'to_email'
    recipient: baseParams.to_email || baseParams.email, // Another possible parameter name
    client_name: baseParams.to_name || baseParams.client_name, // Ensure client name is passed properly
    // Include phone in ALL possible formats for the template
    phone: baseParams.from_phone || baseParams.phone || baseParams.client_phone || 'No proporcionado',
    to_phone: baseParams.from_phone || baseParams.phone || baseParams.client_phone || 'No proporcionado',
    from_phone: baseParams.from_phone || baseParams.phone || baseParams.client_phone || 'No proporcionado',
    // Additional client contact info
    client_contact_email: baseParams.client_email || baseParams.email,
    client_contact_phone: baseParams.client_phone || baseParams.phone || baseParams.from_phone || 'No proporcionado',
    // Customer data in additional formats
    customer_name: baseParams.client_name || baseParams.to_name,
    customer_email: baseParams.client_email || baseParams.email,
    customer_phone: baseParams.client_phone || baseParams.phone || baseParams.from_phone || 'No proporcionado',
    // Ensure privacy policy and consent info are included in all parameter formats
    privacy_policy_text: baseParams.privacy_policy || "Al enviar este formulario, has aceptado nuestra Política de Privacidad.",
    consent_information: baseParams.consent_info,
    // Make sure the HTML section is also included
    privacy_section: baseParams.privacy_section || `
      <div style="margin-top: 20px; padding: 15px; border: 1px solid #ddd; background-color: #f9f9f9; border-radius: 5px;">
        <p style="margin: 0;"><strong>Política de Privacidad:</strong> Al enviar este formulario, has aceptado nuestra 
        <a href="${baseParams.privacy_policy_url || 'https://gavinsurance.com/privacidad'}">Política de Privacidad</a>.</p>
        <p style="margin-top: 10px;"><strong>Consentimiento de Contacto:</strong> ${baseParams.consent_info || "Información de consentimiento no disponible."}</p>
      </div>
    `
  };
};
